import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { OFFER_STAGE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { CheckCircle2 } from "lucide-react";
import { useCallback } from "react";
import { useQuery } from "urql";

interface OfferData {
  id: string;
  status: string;
  baseSalary: number | null;
  currency: string | null;
  bonusDetails: string | null;
  equityDetails: string | null;
  startDate: string | null;
  notes: string | null;
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  candidate: {
    id: string;
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
  };
  offers: OfferData[];
}

function formatSalary(amount: number | null, currency: string | null): string {
  if (amount == null) return "";
  const curr = currency ?? "GBP";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: curr,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${curr} ${amount.toLocaleString()}`;
  }
}

interface PlacementStageProps {
  campaignId: string;
}

export function PlacementStage({ campaignId }: PlacementStageProps) {
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: OFFER_STAGE_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  const placedCandidates = allCandidates.filter((cc) => cc.stage === "placed");

  if (fetching && allCandidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading placements...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-green-500" />
          <h3 className="text-sm font-medium text-text-secondary">Placement</h3>
          <span className="text-xs text-text-faint">{placedCandidates.length} placed</span>
        </div>

        {error ? (
          <QueryErrorState error={error} what="placements" onRetry={refresh} retrying={fetching} />
        ) : placedCandidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-faint">
            No candidates placed yet. Move candidates from the Offer stage.
          </p>
        ) : (
          <div className="space-y-3">
            {placedCandidates.map((cc) => {
              const acceptedOffer = cc.offers.find((o) => o.status === "accepted") ?? cc.offers[0];
              return (
                <div
                  key={cc.id}
                  className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-text-primary">
                        {typeof cc.candidate.referenceNumber === "number" && (
                          <>
                            <CandidateReferenceBadge
                              referenceNumber={cc.candidate.referenceNumber}
                            />{" "}
                          </>
                        )}
                        {cc.candidate.fullName}
                      </h4>
                      {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
                        <p className="text-xs text-text-muted">
                          {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                            .filter(Boolean)
                            .join(" at ")}
                        </p>
                      )}
                    </div>
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                      Placed
                    </span>
                  </div>

                  {acceptedOffer && (
                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                      {acceptedOffer.baseSalary != null && (
                        <div>
                          <span className="text-text-muted">Salary</span>
                          <p className="font-medium text-text-primary">
                            {formatSalary(acceptedOffer.baseSalary, acceptedOffer.currency)}
                          </p>
                        </div>
                      )}
                      {acceptedOffer.bonusDetails && (
                        <div>
                          <span className="text-text-muted">Bonus</span>
                          <p className="text-text-secondary">{acceptedOffer.bonusDetails}</p>
                        </div>
                      )}
                      {acceptedOffer.equityDetails && (
                        <div>
                          <span className="text-text-muted">Equity</span>
                          <p className="text-text-secondary">{acceptedOffer.equityDetails}</p>
                        </div>
                      )}
                      {acceptedOffer.startDate && (
                        <div>
                          <span className="text-text-muted">Start Date</span>
                          <p className="text-text-secondary">{acceptedOffer.startDate}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
